import { useState, useMemo } from 'react';
import { StoreCategory } from '../../models';

interface CategorySidebarProps {
  categories: StoreCategory[];
  selectedCategoryIds: number[];
  onCategoryChange: (ids: number[]) => void;
}

export function CategorySidebar({
  categories,
  selectedCategoryIds,
  onCategoryChange,
}: CategorySidebarProps) {
  const [searchInput, setSearchInput] = useState('');

  const filteredCategories = useMemo(() => {
    if (!searchInput.trim()) return categories;
    return categories.filter((cat) =>
      cat.name.toLowerCase().includes(searchInput.toLowerCase())
    );
  }, [categories, searchInput]);

  const handleCategoryToggle = (categoryId: number) => {
    if (selectedCategoryIds.includes(categoryId)) {
      onCategoryChange(selectedCategoryIds.filter((id) => id !== categoryId));
    } else {
      onCategoryChange([...selectedCategoryIds, categoryId]);
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
  };

  const handleClearAll = () => {
    onCategoryChange([]);
  };

  return (
    <div className="w-full md:w-64 flex-shrink-0">
      <div className="bg-white rounded-xl shadow-sm p-4 sticky top-20">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-text-dark">
            Categorias
            {selectedCategoryIds.length > 0 && (
              <span className="bg-chocolate text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-medium">
                {selectedCategoryIds.length}
              </span>
            )}
          </div>
          <button
            onClick={handleClearAll}
            disabled={selectedCategoryIds.length === 0}
            className="flex items-center gap-1 text-xs text-text-light hover:text-chocolate transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-text-light"
          >
            <svg
              className="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Limpar
          </button>
        </div>

        <div className="relative mb-4">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-light pointer-events-none"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Buscar categoria..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-8 py-2 rounded-lg border border-champagne-dark text-text-dark placeholder-text-light bg-white focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate transition-colors text-sm"
          />
          {searchInput && (
            <button
              onClick={handleClearSearch}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-light hover:text-chocolate transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {filteredCategories.length > 0 ? (
            filteredCategories.map((category) => (
              <label
                key={category.id}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-champagne-light cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedCategoryIds.includes(category.id)}
                  onChange={() => handleCategoryToggle(category.id)}
                  className="w-4 h-4 rounded border-champagne-dark text-chocolate focus:ring-chocolate/20 cursor-pointer"
                />
                <span className="text-sm text-text-dark flex-1">{category.name}</span>
              </label>
            ))
          ) : (
            <p className="text-xs text-text-light py-4 text-center">
              Nenhuma categoria encontrada
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
